import type { ReactElement } from 'react'
import { Link } from 'react-router'
import { useI18n } from '../i18n/useI18n'
import { formatCalendarDate } from '../i18n/format'
import type { I18nContextValue, Lang } from '../i18n/context'
import type { LegalContent, LegalDocument } from '../data/legal/legalDocument'
import { legalPageList } from '../data/legal/legalPages'
import { company } from '../data/company'
import OrgNumber from '../components/OrgNumber'
import type { LegalPageEntry } from '../data/legal/legalPages'
import './legal.css'

function describeVersion(document: LegalDocument, lang: Lang, t: I18nContextValue['t']): string {
  if (document.effectiveDate === null) return t('legal.metaDraft', { version: document.version })
  return t('legal.metaInForce', { version: document.version, date: formatCalendarDate(document.effectiveDate, lang) })
}

export default function Legal({ page }: { page: LegalPageEntry }): ReactElement {
  const { lang, t } = useI18n()
  const content: LegalContent = page.document.content[lang]

  return (
    <section className="section lg-page">
      <div className="container">
        <div className="lg-doc">
          <div className="lg-print-header">{company.legalName} · <OrgNumber /> · {new URL(company.website).host}{page.path}</div>
          <header className="lg-head">
            <h1 className="lg-title">{content.title}</h1>
            <p className="lg-meta">{describeVersion(page.document, lang, t)}</p>
            {page.document.status === 'draft' && (
              <p className="lg-draft" role="note"><i className="bi bi-pencil-square" aria-hidden="true" /> {t('legal.draftBanner')}</p>
            )}
            <p className="lg-intro">{content.intro}</p>
          </header>

          <nav className="lg-toc" aria-label={t('legal.contents')}>
            <div className="lg-toc-label">{t('legal.contents')}</div>
            <ol>
              {content.sections.map((s) => <li key={s.id}><a href={`#${s.id}`}>{s.heading}</a></li>)}
            </ol>
          </nav>

          {content.sections.map((s, i) => (
            <section key={s.id} id={s.id} className="lg-section">
              <h2 className="lg-section-title"><span className="lg-section-num">{i + 1}.</span> {s.heading}</h2>
              {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
            </section>
          ))}

          <nav className="lg-related" aria-label={t('legal.related')}>
            <div className="lg-toc-label">{t('legal.related')}</div>
            {legalPageList.filter((p) => p.id !== page.id).map((p) => <Link key={p.id} to={p.path}>{p.document.content[lang].title}</Link>)}
          </nav>
        </div>
      </div>
    </section>
  )
}
